import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, cx } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { adminApi, formatDateTime, type AdminRow } from '../../lib/adminApi';
import { useAdminAuth } from '../../lib/adminAuth';
import { usePageTitle } from '../../lib/usePageTitle';
import { AdminPanelState } from './AdminLayout';

type Tab = 'profile' | 'security' | 'admins' | 'audit';

const TABS: { id: Tab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'security', label: 'Security' },
  { id: 'admins', label: 'Admin users' },
  { id: 'audit', label: 'Audit log' },
];

export function AdminSettings() {
  usePageTitle('Settings — WaveQA Admin');

  const [tab, setTab] = useState<Tab>('profile');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white">Settings</h1>
        <p className="mt-1 text-sm text-hull-400">
          Your account, and who else can reach this portal.
        </p>
      </div>

      <div role="tablist" aria-label="Settings" className="flex gap-1 overflow-x-auto border-b border-hull-800">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            id={`tab-${entry.id}`}
            aria-selected={tab === entry.id}
            aria-controls={`panel-${entry.id}`}
            onClick={() => setTab(entry.id)}
            className={cx(
              'whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition',
              tab === entry.id
                ? 'border-surge-400 font-medium text-white'
                : 'border-transparent text-hull-400 hover:text-hull-200',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {tab === 'profile' && <ProfilePanel />}
        {tab === 'security' && <SecurityPanel />}
        {tab === 'admins' && <AdminsPanel />}
        {tab === 'audit' && <AuditPanel />}
      </div>
    </div>
  );
}

function ProfilePanel() {
  const { admin } = useAdminAuth();
  if (!admin) return null;

  return (
    <dl className="grid gap-4 rounded-card border border-hull-700 bg-hull-800/60 p-6 sm:grid-cols-2">
      <div>
        <dt className="text-xs text-hull-400">Email</dt>
        <dd className="mt-1 break-all text-sm text-hull-200">{admin.email}</dd>
      </div>
      <div>
        <dt className="text-xs text-hull-400">Name</dt>
        <dd className="mt-1 text-sm text-hull-200">{admin.display_name ?? '—'}</dd>
      </div>
      <div>
        <dt className="text-xs text-hull-400">Role</dt>
        <dd className="mt-1 text-sm text-hull-200">
          Administrator
          <span className="ml-2 text-xs text-hull-500">
            the portal has one privilege level
          </span>
        </dd>
      </div>
      <div>
        <dt className="text-xs text-hull-400">Two-factor</dt>
        <dd className="mt-1 text-sm text-hull-200">
          {admin.mfa_enabled ? 'On' : 'Off'}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-hull-400">Last sign-in</dt>
        <dd className="mt-1 text-sm text-hull-200">{formatDateTime(admin.last_login_at)}</dd>
      </div>
      <div>
        <dt className="text-xs text-hull-400">Password last changed</dt>
        <dd className="mt-1 text-sm text-hull-200">
          {formatDateTime(admin.password_changed_at)}
        </dd>
      </div>
    </dl>
  );
}

function SecurityPanel() {
  const { admin, signOut } = useAdminAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string[]>>({});
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () =>
      adminApi.changePassword({
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      }),
    onSuccess: () => {
      setError(null);
      setFields({});
      setDone(true);
      // Changing the password revokes every session, this one included — so the
      // only correct next state is signed out. Leaving the operator on a screen
      // backed by a dead token would show 401s instead.
      window.setTimeout(() => void signOut(), 2500);
    },
    onError: (cause) => {
      if (cause instanceof ApiError) {
        setError(cause.message);
        setFields(cause.fields ?? {});
      } else {
        setError('Could not change the password.');
      }
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    change.mutate();
  }

  return (
    <div className="space-y-6">
      <section className="rounded-card border border-hull-700 bg-hull-800/60 p-6">
        <h2 className="text-base font-semibold text-white">Change password</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-hull-400">
          Every session you hold is ended, including this one. If the reason for the
          change is that the old password leaked, leaving those sessions alive would
          defeat it.
        </p>

        {done ? (
          <Alert tone="success">Password changed. Signing you out…</Alert>
        ) : (
          <form onSubmit={submit} noValidate className="mt-5 max-w-sm">
            {error && <Alert tone="error">{error}</Alert>}

            <Field label="Current password" htmlFor="current-password">
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                required
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            </Field>

            <Field label="New password" htmlFor="new-password" errors={fields.new_password}>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                invalid={Boolean(fields.new_password)}
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
            </Field>

            <Field
              label="Confirm new password"
              htmlFor="confirm-password"
              errors={fields.confirm_password}
            >
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                invalid={Boolean(fields.confirm_password)}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </Field>

            <Button type="submit" disabled={change.isPending}>
              {change.isPending ? 'Saving…' : 'Change password'}
            </Button>
          </form>
        )}
      </section>

      <section className="rounded-card border border-hull-700 bg-hull-800/60 p-6">
        <h2 className="text-base font-semibold text-white">Two-factor authentication</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-hull-400">
          Currently <strong className="text-hull-200">{admin?.mfa_enabled ? 'on' : 'off'}</strong>.
          A sign-in code is emailed to your address after your password is accepted.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-hull-400">
          You cannot change your own setting — only another administrator can, from the
          Admin users tab. Turning off your own second factor is the most useful single
          action for someone holding a stolen session, and it is the one change here with
          no legitimate urgency.
        </p>
      </section>
    </div>
  );
}

function AdminsPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string[]>>({});
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const admins = useQuery({ queryKey: ['admin', 'admins'], queryFn: () => adminApi.getAdmins() });

  function onSettled() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
  }

  function onFailure(cause: unknown) {
    if (cause instanceof ApiError) {
      setError(cause.message);
      setFields(cause.fields ?? {});
    } else {
      setError('That change did not go through.');
    }
  }

  const create = useMutation({
    mutationFn: () =>
      adminApi.createAdmin({ email, password, display_name: name.trim() || null }),
    onSuccess: () => {
      setError(null);
      setFields({});
      setCreating(false);
      setEmail('');
      setPassword('');
      setName('');
      onSettled();
    },
    onError: onFailure,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
      adminApi.setAdminStatus(id, status),
    onSuccess: () => {
      setError(null);
      onSettled();
    },
    onError: onFailure,
  });

  const setMfa = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      adminApi.setAdminMfa(id, enabled),
    onSuccess: () => {
      setError(null);
      onSettled();
    },
    onError: onFailure,
  });

  return (
    <div className="space-y-5">
      {error && <Alert tone="error">{error}</Alert>}

      <AdminPanelState loading={admins.isPending} error={admins.error}>
        {admins.data && (
          <ul className="space-y-3">
            {admins.data.map((row) => (
              <AdminCard
                key={row.id}
                row={row}
                busy={setStatus.isPending || setMfa.isPending}
                onToggleStatus={() =>
                  setStatus.mutate({
                    id: row.id,
                    status: row.status === 'active' ? 'disabled' : 'active',
                  })
                }
                onToggleMfa={() => setMfa.mutate({ id: row.id, enabled: !row.mfa_enabled })}
              />
            ))}
          </ul>
        )}
      </AdminPanelState>

      {creating ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
          noValidate
          className="max-w-sm rounded-card border border-hull-700 bg-hull-800/60 p-6"
        >
          <h2 className="text-base font-semibold text-white">Add an administrator</h2>
          <p className="mb-5 mt-1.5 text-sm leading-relaxed text-hull-400">
            Set the initial password here and give it to them in person or over a channel
            you trust. It is deliberately not emailed — a credential with this much
            authority should not be sitting in a mailbox.
          </p>
          <p className="mb-5 text-sm leading-relaxed text-hull-400">
            The address must also be on the server&rsquo;s admin sign-in allowlist
            (<code className="text-hull-300">WAVE_ADMIN_ALLOWED_EMAILS</code>), or the
            account could never be used and is refused here rather than created.
          </p>

          <Field label="Email" htmlFor="new-admin-email" errors={fields.email}>
            <Input
              id="new-admin-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Name" htmlFor="new-admin-name">
            <Input
              id="new-admin-name"
              value={name}
              maxLength={128}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field
            label="Initial password"
            htmlFor="new-admin-password"
            errors={fields.password}
          >
            <Input
              id="new-admin-password"
              type="password"
              autoComplete="new-password"
              required
              invalid={Boolean(fields.password)}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <div className="flex gap-3">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create administrator'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCreating(false);
                setError(null);
                setFields({});
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="secondary" onClick={() => setCreating(true)}>
          Add an administrator
        </Button>
      )}
    </div>
  );
}

function AdminCard({
  row,
  busy,
  onToggleStatus,
  onToggleMfa,
}: {
  row: AdminRow;
  busy: boolean;
  onToggleStatus: () => void;
  onToggleMfa: () => void;
}) {
  const disabled = row.status !== 'active';

  return (
    <li className="flex flex-wrap items-center justify-between gap-4 rounded-card border border-hull-700 bg-hull-800/60 p-4">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 font-medium text-white">
          <span className="truncate">{row.display_name ?? row.email}</span>
          {row.is_self && (
            <span className="rounded-full bg-hull-700 px-2 py-0.5 text-xs font-normal text-hull-300">
              you
            </span>
          )}
          {disabled && (
            <span className="rounded-full border border-fail-600/40 bg-fail-600/10 px-2 py-0.5 text-xs font-normal text-fail-400">
              disabled
            </span>
          )}
          {!row.mfa_enabled && (
            <span className="rounded-full border border-warn-600/40 bg-warn-600/10 px-2 py-0.5 text-xs font-normal text-warn-400">
              2FA off
            </span>
          )}
          {/* The row exists but the server will not let this address sign in.
              Worth stating: otherwise it looks like working access. */}
          {!row.can_sign_in && (
            <span className="rounded-full border border-fail-600/40 bg-fail-600/10 px-2 py-0.5 text-xs font-normal text-fail-400">
              cannot sign in
            </span>
          )}
        </p>
        <p className="mt-0.5 truncate text-xs text-hull-400">{row.email}</p>
        <p className="mt-0.5 text-xs text-hull-500">
          {row.last_login_at
            ? `Last signed in ${formatDateTime(row.last_login_at)}`
            : 'Has never signed in'}
          {!row.recently_active && row.last_login_at && ' · dormant'}
        </p>
      </div>

      {/* Both controls refuse to act on oneself server-side. Disabling them here
          means the operator is not offered a button that can only answer 409. */}
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button variant="ghost" disabled={row.is_self || busy} onClick={onToggleMfa}>
          {row.mfa_enabled ? 'Turn 2FA off' : 'Turn 2FA on'}
        </Button>
        <Button variant="secondary" disabled={row.is_self || busy} onClick={onToggleStatus}>
          {disabled ? 'Re-enable' : 'Disable'}
        </Button>
      </div>
    </li>
  );
}

function AuditPanel() {
  const [page, setPage] = useState(1);
  const audit = useQuery({
    queryKey: ['admin', 'audit', page],
    queryFn: () => adminApi.getAudit(page),
  });

  const pages = audit.data ? Math.max(1, Math.ceil(audit.data.total / audit.data.page_size)) : 1;

  return (
    <div className="space-y-4">
      <p className="text-sm text-hull-400">
        Every administrator action, newest first. Customer sign-ins and agent runs are
        recorded on the same trail but are not shown here.
      </p>

      <AdminPanelState
        loading={audit.isPending}
        error={audit.error}
        empty={audit.data?.rows.length === 0}
        emptyTitle="Nothing recorded yet"
        emptyBody="Administrator actions will appear here as they happen."
      >
        {audit.data && audit.data.rows.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-card border border-hull-700">
              <table className="w-full min-w-[42rem] text-left text-sm">
                <thead className="bg-hull-800/60 text-xs uppercase tracking-wider text-hull-400">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Action
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Administrator
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Detail
                    </th>
                    <th scope="col" className="px-4 py-3 font-medium">
                      When
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {audit.data.rows.map((row) => (
                    <tr key={row.id} className="border-t border-hull-800">
                      <td
                        className={cx(
                          'px-4 py-3',
                          row.succeeded ? 'text-hull-200' : 'text-fail-400',
                        )}
                      >
                        {row.event_type.replace(/_/g, ' ').toLowerCase()}
                      </td>
                      <td className="px-4 py-3 text-hull-300">
                        {row.actor_admin_email ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-hull-400">
                        {row.reason && <span className="block">{row.reason}</span>}
                        {row.metadata?.target_email && (
                          <span className="block">{row.metadata.target_email}</span>
                        )}
                        {row.ip_address && (
                          <span className="block text-hull-500">{row.ip_address}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-hull-300">
                        {formatDateTime(row.occurred_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <nav
                aria-label="Audit pagination"
                className="flex items-center justify-between gap-4 text-sm"
              >
                <Button
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <span className="text-hull-400">
                  Page {page} of {pages}
                </span>
                <Button
                  variant="secondary"
                  disabled={page >= pages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </nav>
            )}
          </>
        )}
      </AdminPanelState>
    </div>
  );
}
